/**
 * Dateien, archivierte Dokumente und Wissensseiten.
 *
 * `07` §7.12 unterscheidet **fachlich**: Arbeitsdatei (Nextcloud),
 * archiviertes Dokument (Paperless), Wissen (Collectives). Die technische
 * Herkunft ist ein sekundärer Hinweis, der primäre Zugriff läuft über den
 * Auftrag. Deshalb trägt jedes Objekt hier eine `art` — und der Provider
 * steckt im Ref, wo er beim Anzeigen nicht im Weg ist.
 *
 * Die Offlinekennzeichnung ist keine Zierde. `07` §7.9 verlangt, dass die
 * Monteur-App zeigt, wenn ein Inhalt **nicht** vorgeladen wurde — und dass
 * große Dateien nur auf Wunsch gespeichert werden. Ein Bestand, in dem alles
 * `vorgeladen` ist, prüft die Warnung nie, die im Funkloch den Unterschied
 * macht.
 */

import type { Ablageobjekt } from "./modell.js";
import { DATEI, DOKUMENT, WISSEN } from "./kennungen.js";
import { ref } from "./modell.js";
import { zeitpunkt } from "./zeit.js";

export function ablage(bezugstag: Date): readonly Ablageobjekt[] {
  return [
    // ───────── Arbeitsdateien in Nextcloud ─────────
    {
      ref: DATEI.schaltplanKuehlung,
      art: "arbeitsdatei",
      name: "Schaltplan Kaelteregler KR-40.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 412_880,
      geaendert: zeitpunkt(bezugstag, -9, 11, 30),
      geaendertVon: "Sabine Kern",
      pfad: "/Auftraege/2481 Baeckerei Hufnagel/Unterlagen",
      offline: "vorgeladen",
      schlagworte: ["Schaltplan", "Kühlung", "E9"],
      /*
       * Drei Fassungen, und die mittlere ist der Grund, aus dem es die
       * Wiederherstellung gibt: Jemand hat den Plan überschrieben, und die
       * Anmerkungen des Vorgängers waren weg. `NC-003` holt sie zurück.
       */
      versionen: [
        {
          kennung: "v3",
          zeitpunkt: zeitpunkt(bezugstag, -9, 11, 30),
          groesseBytes: 412_880,
          geaendertVon: "Sabine Kern",
          notiz: "Klemmenplan des Ersatzteils ergänzt",
        },
        {
          kennung: "v2",
          zeitpunkt: zeitpunkt(bezugstag, -40, 16, 5),
          groesseBytes: 388_112,
          geaendertVon: "Andreas Wagner",
          notiz: "vom Hersteller überschrieben — Anmerkungen gingen verloren",
        },
        {
          kennung: "v1",
          zeitpunkt: zeitpunkt(bezugstag, -400, 9, 0),
          groesseBytes: 402_004,
          geaendertVon: "Andreas Wagner",
        },
      ],
    },
    {
      ref: DATEI.fotoKuehlungVorher,
      art: "arbeitsdatei",
      name: "2026-08-29 Fehlerspeicher.jpg",
      mimetyp: "image/jpeg",
      groesseBytes: 2_140_552,
      geaendert: zeitpunkt(bezugstag, -7, 8, 40),
      geaendertVon: "Marek Liszka",
      pfad: "/Auftraege/2481 Baeckerei Hufnagel/Fotos",
      offline: "auf-wunsch",
    },
    {
      ref: DATEI.fotoKuehlungNachher,
      art: "arbeitsdatei",
      name: "Typenschild Steuerplatine.jpg",
      mimetyp: "image/jpeg",
      groesseBytes: 1_882_004,
      geaendert: zeitpunkt(bezugstag, 0, 9, 5),
      geaendertVon: "Marek Liszka",
      pfad: "/Auftraege/2481 Baeckerei Hufnagel/Fotos",
      offline: "auf-wunsch",
    },
    {
      /*
       * Im Papierkorb. `NC-003` holt sie zurück, und `07` §7.12 macht den
       * Dateibereich zur Einstiegsstelle dafür — der Fall „gestern war die
       * Datei noch da" landet sonst beim Betreiber statt beim Benutzer.
       */
      ref: ref("nextcloud", "file", "f-01n020"),
      art: "arbeitsdatei",
      name: "Angebot Kuehlung 2481 (Entwurf).odt",
      mimetyp: "application/vnd.oasis.opendocument.text",
      groesseBytes: 44_112,
      geaendert: zeitpunkt(bezugstag, -11, 14, 20),
      geaendertVon: "Sabine Kern",
      pfad: "/Auftraege/2481 Baeckerei Hufnagel",
      offline: "nur-online",
      geloescht: zeitpunkt(bezugstag, -2, 9, 40),
      geloeschtVon: "Sabine Kern",
    },
    {
      ref: DATEI.aufmassTreppenhaus,
      art: "arbeitsdatei",
      name: "Aufmass Treppenhaus.ods",
      mimetyp: "application/vnd.oasis.opendocument.spreadsheet",
      groesseBytes: 28_400,
      geaendert: zeitpunkt(bezugstag, -21, 15, 12),
      geaendertVon: "Sabine Kern",
      pfad: "/Auftraege/2483 Zeiler und Partner",
      offline: "vorgeladen",
    },
    {
      ref: DATEI.datenblattLeuchte,
      art: "arbeitsdatei",
      name: "Datenblatt LED-Wannenleuchte 1200.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 611_200,
      geaendert: zeitpunkt(bezugstag, -21, 15, 20),
      pfad: "/Auftraege/2483 Zeiler und Partner/Unterlagen",
      offline: "vorgeladen",
    },
    {
      ref: DATEI.fotoHebebuehne,
      art: "arbeitsdatei",
      name: "Hebebuehne 2 Typenschild.jpg",
      mimetyp: "image/jpeg",
      groesseBytes: 1_402_118,
      geaendert: zeitpunkt(bezugstag, 0, 14, 15),
      geaendertVon: "Tobias Reuther",
      pfad: "/Auftraege/2486 Autohaus Merkle/Fotos",
      offline: "auf-wunsch",
    },
    {
      ref: DATEI.planWallbox,
      art: "arbeitsdatei",
      name: "Lageplan Garage.png",
      mimetyp: "image/png",
      groesseBytes: 844_600,
      geaendert: zeitpunkt(bezugstag, -14, 10, 0),
      pfad: "/Auftraege/2490 Brandt",
      offline: "vorgeladen",
    },
    {
      ref: DATEI.anschlussschemaWallbox,
      art: "arbeitsdatei",
      name: "Anschlussschema Wallbox 11kW.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 380_100,
      geaendert: zeitpunkt(bezugstag, -14, 10, 5),
      pfad: "/Auftraege/2490 Brandt/Unterlagen",
      offline: "vorgeladen",
    },
    {
      ref: DATEI.grundrissPraxis,
      art: "arbeitsdatei",
      name: "Grundriss Behandlungsraum 3.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 1_204_770,
      geaendert: zeitpunkt(bezugstag, -8, 16, 40),
      pfad: "/Auftraege/2499 Praxis Hebling",
      offline: "vorgeladen",
    },
    {
      ref: DATEI.fotoZaehlerschrank,
      art: "arbeitsdatei",
      name: "Zaehlerschrank Bestand.jpg",
      mimetyp: "image/jpeg",
      groesseBytes: 2_902_331,
      geaendert: zeitpunkt(bezugstag, -3, 12, 0),
      geaendertVon: "Tobias Reuther",
      pfad: "/Auftraege/2507 Zeiler und Partner",
      offline: "auf-wunsch",
    },
    {
      // Eine Sprachnotiz. `07` §7.14 führt sie als Alternative zu langen
      // Texteingaben — mit Handschuhen tippt niemand einen Absatz.
      ref: DATEI.sprachnotizStoerung,
      art: "arbeitsdatei",
      name: "Sprachnotiz Notdienst.m4a",
      mimetyp: "audio/mp4",
      groesseBytes: 386_240,
      geaendert: zeitpunkt(bezugstag, 0, 7, 48),
      geaendertVon: "Marek Liszka",
      pfad: "/Auftraege/2505 Baeckerei Hufnagel",
      offline: "vorgeladen",
    },
    {
      ref: DATEI.berichtPdfKuehlung,
      art: "arbeitsdatei",
      name: "Servicebericht 2481-1.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 198_440,
      geaendert: zeitpunkt(bezugstag, -6, 9, 0),
      pfad: "/Auftraege/2481 Baeckerei Hufnagel/Berichte",
      offline: "nur-online",
    },
    {
      ref: DATEI.berichtPdfTreppenhaus,
      art: "arbeitsdatei",
      name: "Servicebericht 2483-1.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 204_112,
      geaendert: zeitpunkt(bezugstag, -4, 10, 30),
      pfad: "/Auftraege/2483 Zeiler und Partner/Berichte",
      offline: "nur-online",
    },
    {
      // Absichtlich groß und absichtlich nicht vorgeladen: Das ist der Fall,
      // für den `07` §7.9 die Unterscheidung „optional" überhaupt eingeführt
      // hat. Ein 84-MB-Handbuch gehört nicht ungefragt aufs Monteurhandy.
      ref: DATEI.handbuchHebebuehne,
      art: "arbeitsdatei",
      name: "Herstellerhandbuch Hebebuehne Typ HB-3200.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 84_220_916,
      geaendert: zeitpunkt(bezugstag, -400, 12, 0),
      pfad: "/Wissen/Herstellerunterlagen",
      offline: "auf-wunsch",
      schlagworte: ["Hebebühne", "Handbuch", "HB-3200"],
    },

    // ───────── archivierte Dokumente in Paperless ─────────
    {
      ref: DOKUMENT.rechnungElektrogrosshandel,
      art: "dokument",
      name: "Rechnung Elektrogrosshandel Nord 2026-114237.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 142_880,
      geaendert: zeitpunkt(bezugstag, 0, 6, 12),
      offline: "nur-online",
      schlagworte: ["Eingangsrechnung", "ZUGFeRD", "2026"],
    },
    {
      ref: DOKUMENT.rechnungKabelwerk,
      art: "dokument",
      name: "XRechnung Kabelwerk Sued 4471-2026.xml",
      mimetyp: "application/xml",
      groesseBytes: 38_204,
      geaendert: zeitpunkt(bezugstag, -1, 14, 38),
      offline: "nur-online",
      schlagworte: ["Eingangsrechnung", "XRechnung", "2026"],
    },
    {
      ref: DOKUMENT.rechnungFehlerhaft,
      art: "dokument",
      name: "Rechnung Leuchtenhandel Ost LH-2026-0912.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 220_118,
      geaendert: zeitpunkt(bezugstag, -1, 9, 5),
      offline: "nur-online",
      schlagworte: ["Eingangsrechnung", "ZUGFeRD", "Validierungsfehler"],
    },
    {
      ref: DOKUMENT.pruefprotokoll2025,
      art: "dokument",
      name: "Pruefprotokoll Hebebuehnen Vorjahr.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 604_220,
      geaendert: zeitpunkt(bezugstag, -6, 16, 2),
      offline: "vorgeladen",
      schlagworte: ["Prüfprotokoll", "Autohaus Merkle"],
    },
    {
      ref: DOKUMENT.wartungsvertragSeniorenheim,
      art: "dokument",
      name: "Wartungsvertrag Rufanlage St. Elisabeth.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 288_440,
      geaendert: zeitpunkt(bezugstag, -25, 9, 0),
      offline: "nur-online",
      schlagworte: ["Vertrag", "Wartung", "Rufanlage"],
    },
    {
      ref: DOKUMENT.lieferschein,
      art: "dokument",
      name: "Lieferschein 77-2026-4412.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 96_220,
      geaendert: zeitpunkt(bezugstag, -1, 11, 20),
      offline: "nur-online",
      schlagworte: ["Lieferschein", "Metallbau Grasser"],
    },
    {
      ref: DOKUMENT.abnahmeprotokollWallbox,
      art: "dokument",
      name: "Netzbetreiberanmeldung Wallbox Brandt.pdf",
      mimetyp: "application/pdf",
      groesseBytes: 174_002,
      geaendert: zeitpunkt(bezugstag, -14, 9, 0),
      offline: "vorgeladen",
      schlagworte: ["Anmeldung", "Wallbox"],
    },

    // ───────── Wissen aus Collectives ─────────
    {
      ref: WISSEN.fehlercodesKuehlaggregat,
      art: "wissen",
      name: "Fehlercodes Kälteregler KR-40",
      mimetyp: "text/markdown",
      groesseBytes: 4_820,
      geaendert: zeitpunkt(bezugstag, -60, 14, 0),
      offline: "vorgeladen",
      schlagworte: ["Kühlung", "Fehlercode", "E9", "KR-40"],
      inhalt:
        "## Fehlercodes Kälteregler KR-40\n\n"
        + "**E7** – Fühlerbruch Verdampfer. Fühler PT1000 prüfen, Klemmstelle auf "
        + "Feuchtigkeit kontrollieren.\n\n"
        + "**E9** – Wiederholte Hochdruckabschaltung. Reihenfolge der Prüfung: "
        + "Verflüssiger reinigen, Lüfterdrehzahl messen, Druckschalter prüfen. "
        + "Tritt E9 nach Reinigung erneut auf, ist erfahrungsgemäß die Steuerplatine "
        + "betroffen — Parameter vor dem Tausch auslesen, sie gehen sonst verloren.\n\n"
        + "**E12** – Kommunikationsfehler zum Bedienteil. Busleitung und Abschluss prüfen.",
    },
    {
      ref: WISSEN.wallboxAnschluss,
      art: "wissen",
      name: "Wallbox: Anschluss und Anmeldung",
      mimetyp: "text/markdown",
      groesseBytes: 6_110,
      geaendert: zeitpunkt(bezugstag, -120, 9, 30),
      offline: "vorgeladen",
      schlagworte: ["Wallbox", "Anmeldung", "FI Typ B"],
      inhalt:
        "## Wallbox: Anschluss und Anmeldung\n\n"
        + "Bis 11 kW genügt die Anmeldung beim Netzbetreiber, ab 12 kW ist eine "
        + "Genehmigung erforderlich. Anmeldung **vor** Montage einreichen.\n\n"
        + "FI Typ B ist Pflicht, sofern die Wallbox nicht selbst eine "
        + "DC-Fehlerstromerkennung mitbringt — Datenblatt prüfen, nicht raten.\n\n"
        + "Messprotokoll der Inbetriebnahme gehört in den Servicebericht, nicht nur "
        + "in den Ordner.",
    },
    {
      ref: WISSEN.pruefungBetriebsmittel,
      art: "wissen",
      name: "Prüfung ortsveränderlicher Betriebsmittel – Ablauf",
      mimetyp: "text/markdown",
      groesseBytes: 5_204,
      geaendert: zeitpunkt(bezugstag, -200, 11, 0),
      offline: "vorgeladen",
      schlagworte: ["Prüfung", "DGUV V3", "Betriebsmittel"],
      inhalt:
        "## Prüfung ortsveränderlicher Betriebsmittel\n\n"
        + "Reihenfolge: Sichtprüfung, Schutzleiterwiderstand, Isolationswiderstand, "
        + "Ersatzableitstrom, Funktionsprüfung.\n\n"
        + "Jedes geprüfte Gerät bekommt eine Plakette mit Monat und Jahr der nächsten "
        + "Prüfung. **Nicht bestandene Geräte werden vor Ort der Nutzung entzogen** und "
        + "im Protokoll namentlich aufgeführt.",
    },
    {
      ref: WISSEN.notdienstAblauf,
      art: "wissen",
      name: "Notdienst – Ablauf und Abrechnung",
      mimetyp: "text/markdown",
      groesseBytes: 3_440,
      geaendert: zeitpunkt(bezugstag, -30, 8, 0),
      offline: "vorgeladen",
      schlagworte: ["Notdienst", "Abrechnung"],
      inhalt:
        "## Notdienst\n\n"
        + "Annahme über die Bereitschaftsnummer. Der Einsatz wird **immer** als eigener "
        + "Auftrag angelegt, auch wenn er zu einem laufenden Auftrag gehört — sonst "
        + "vermischen sich Zuschläge mit der Regelarbeitszeit.\n\n"
        + "Anfahrt und Arbeitszeit getrennt buchen. Zuschlag wird im Büro ergänzt, "
        + "nicht vom Monteur.",
    },
    {
      ref: WISSEN.materialrueckgabe,
      art: "wissen",
      name: "Materialrückgabe und Restbestände",
      mimetyp: "text/markdown",
      groesseBytes: 2_180,
      geaendert: zeitpunkt(bezugstag, -90, 16, 0),
      offline: "auf-wunsch",
      schlagworte: ["Material", "Lager"],
      inhalt:
        "## Materialrückgabe\n\n"
        + "Nicht verbautes Material wird im Einsatz erfasst und beim Rücktransport "
        + "abgezogen — nicht stillschweigend im Fahrzeug gelassen. Was länger als eine "
        + "Woche im Fahrzeug liegt, taucht in keiner Inventur auf.",
    },
  ];
}
